import React, { useState, useEffect, type MouseEvent } from 'react';
import type SwiperCore from 'swiper';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs } from 'swiper/modules';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faTimes, faSearchPlus } from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import API_ENDPOINTS, { IMAGE_BASE_URL } from "../../api/apiConfig";
import { FaChevronUp, FaChevronDown, FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa';
import '../../assets/custom.css';
import { useUser } from "../../context/UserContext";
import '../../assets/css/theme.css';
import '../../assets/css/pd.css';
import type { CartItem, Pagination, Product, RatingBreakdown, Review } from '../../types';

// -----------------------------------------------------------------
// Star Row Helper
// -----------------------------------------------------------------
interface StarRowProps {
  rating: number;
  size?: number;
}

const StarRow = ({ rating, size = 16 }: StarRowProps) => {
  return (
    <span className="ffd-stars">
      {[1, 2, 3, 4, 5].map((i) => {
        if (i <= Math.floor(rating)) {
          return <FaStar key={i} size={size} color="#c9a96e" />;
        } else if (i - rating < 1 && rating % 1 >= 0.5) {
          return <FaStarHalfAlt key={i} size={size} color="#c9a96e" />;
        } else {
          return <FaRegStar key={i} size={size} color="#d9cfbe" />;
        }
      })}
    </span>
  );
};

// -----------------------------------------------------------------
// Reviews Section Component
// -----------------------------------------------------------------
interface ReviewsSectionProps {
  productId: number;
}

const REVIEWS_PER_PAGE = 5;

const ReviewsSection = ({ productId }: ReviewsSectionProps) => {
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [breakdown, setBreakdown] = useState<RatingBreakdown>({});
  const [reviews, setReviews] = useState<Review[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<Pagination | null>(null);

  const fetchReviews = async (page = 1, append = false) => {
    if (page === 1) setReviewsLoading(true);
    else setLoadingMore(true);

    try {
      const response = await fetch(API_ENDPOINTS.GET_PRODUCT_REVIEWS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productId,
          page,
          per_page: REVIEWS_PER_PAGE,
        }),
      });
      const data = await response.json();
      if (data.status === 'success') {
        setAverageRating(data.average_rating || 0);
        setTotalReviews(data.total_reviews || 0);
        setBreakdown(data.rating_breakdown || {});
        setPagination(data.pagination || null);
        setReviews((prev) => append ? [...prev, ...data.data] : data.data);
        setCurrentPage(page);
      }
    } catch (e) {
      console.error('Review fetch error:', e);
    } finally {
      setReviewsLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!productId) return;
    fetchReviews(1, false);
  }, [productId]);

  const handleLoadMore = () => fetchReviews(currentPage + 1, true);

  const handleCollapse = () => {
    setReviews([]);
    setCurrentPage(1);
    fetchReviews(1, false);
    document.getElementById('reviews-section-header')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePageChange = (page: number) => {
    fetchReviews(page, false);
    document.getElementById('reviews-section-header')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const formatDate = (dateStr: string): string => {
    try {
      const dt = new Date(dateStr.replace(' ', 'T'));
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  const getPageNumbers = (): Array<number | string> => {
    if (!pagination) return [];
    const { total_pages, current_page } = pagination;
    if (total_pages <= 5) return Array.from({ length: total_pages }, (_, i) => i + 1);
    if (current_page <= 3) return [1, 2, 3, 4, '...', total_pages];
    if (current_page >= total_pages - 2) return [1, '...', total_pages - 3, total_pages - 2, total_pages - 1, total_pages];
    return [1, '...', current_page - 1, current_page, current_page + 1, '...', total_pages];
  };

  const shown = reviews.length;
  const hasMore = pagination?.has_next;

  return (
    <section className="ffd-reviews" id="reviews-section-header">
      <div className="ffd-container">
        <div className="ffd-rev-head">
          <div>
            <p className="ffh-eyebrow ffh-eyebrow--left">Reviews</p>
            <h2 className="ffd-h2">Customer <em>reviews</em></h2>
          </div>
          {totalReviews > 0 && (
            <span className="ffd-rev-pill">
              <FaStar size={13} color="#c9a96e" />
              {averageRating.toFixed(1)}&nbsp;({totalReviews})
            </span>
          )}
        </div>

        {reviewsLoading ? (
          <div className="ffd-rev-list">
            {[1, 2, 3].map((i) => (
              <div key={i} className="ffd-skel-line" style={{ height: 96, borderRadius: 10 }} />
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <div className="ffd-rev-empty">
            <FaRegStar size={34} color="#d9cfbe" />
            <p>No reviews yet</p>
            <span>Be the first to review this product</span>
          </div>
        ) : (
          <div className="ffd-rev-layout">
            {/* Summary */}
            <div className="ffd-rev-summary">
              <div className="ffd-rev-score">{averageRating.toFixed(1)}</div>
              <StarRow rating={averageRating} size={16} />
              <div className="ffd-rev-count">{totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}</div>

              <div className="ffd-rev-bars">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = breakdown[`${star}_star`] || 0;
                  const fraction = totalReviews > 0 ? count / totalReviews : 0;
                  return (
                    <div key={star} className="ffd-rev-bar">
                      <span>{star}</span>
                      <FaStar size={10} color="#c9a96e" />
                      <div className="ffd-rev-track">
                        <div className="ffd-rev-fill" style={{ width: `${fraction * 100}%` }} />
                      </div>
                      <span className="ffd-rev-n">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* List */}
            <div>
              <p className="ffd-rev-showing">
                Showing <strong>{shown}</strong> of <strong>{totalReviews}</strong> reviews
                {pagination && <> — Page {pagination.current_page} of {pagination.total_pages}</>}
              </p>

              <div className="ffd-rev-list">
                {reviews.map((review) => (
                  <article key={review.id} className="ffd-rev-card">
                    <div className="ffd-rev-top">
                      <span className="ffd-rev-avatar">{(review.user_name || 'A')[0].toUpperCase()}</span>
                      <div className="ffd-rev-who">
                        <div className="ffd-rev-name">
                          {review.user_name || 'Anonymous'}
                          {review.is_verified && <span className="ffd-rev-verified">✓ Verified</span>}
                        </div>
                        <div className="ffd-rev-date">{formatDate(review.created_at)}</div>
                      </div>
                      <StarRow rating={review.rating} size={13} />
                    </div>
                    {review.review && <p className="ffd-rev-text">{review.review}</p>}
                  </article>
                ))}
              </div>

              {hasMore && (
                <button type="button" className="ffh-btn ffh-btn--outline ffd-rev-more" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? 'Loading…' : <><FaChevronDown size={11} /> Load more reviews ({totalReviews - shown} remaining)</>}
                </button>
              )}

              {currentPage > 1 && !hasMore && (
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <button type="button" className="ffd-link" onClick={handleCollapse}>
                    <FaChevronUp size={10} /> Show less
                  </button>
                </div>
              )}

              {pagination && pagination.total_pages > 1 && !hasMore && currentPage === pagination.total_pages && (
                <div className="ffd-pag">
                  <button type="button" className="ffd-pag-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={!pagination.has_previous} aria-label="Previous page">‹</button>
                  {getPageNumbers().map((p, idx) =>
                    p === '...' ? (
                      <span key={`e${idx}`} className="ffd-pag-dots">…</span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        className={`ffd-pag-btn${p === currentPage ? ' is-active' : ''}`}
                        onClick={() => handlePageChange(p as number)}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button type="button" className="ffd-pag-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={!pagination.has_next} aria-label="Next page">›</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// -----------------------------------------------------------------
// Color Variants Component
// -----------------------------------------------------------------
interface ColorVariantsProps {
  product: Product;
  onVariantClick: (slug: string) => void;
}

const ColorVariants = ({ product, onVariantClick }: ColorVariantsProps) => {
  const [hoveredId, setHoveredId] = useState<number | 'current' | null>(null);
  const [brokenImg, setBrokenImg] = useState<Record<number, boolean>>({});

  // Don't render if no color info at all
  if (!product.color && (!product.color_variants || product.color_variants.length === 0)) {
    return null;
  }

  const totalColors = (product.color_variants?.length || 0) + 1;
  const label = hoveredId && hoveredId !== 'current'
    ? (product.color_variants?.find((v) => v.p_id === hoveredId)?.color || product.color || '—')
    : (product.color || '—');

  return (
    <div className="ffd-block">
      <div className="ffd-label-row">
        <span className="ffd-label">Colour: <b>{label}</b></span>
        {totalColors > 1 && (
          <span className="ffd-hint">{totalColors} colours available</span>
        )}
      </div>

      <div className="ffd-swatches">
        {/* current product (selected) */}
        <div
          className="ffd-swatch ffd-swatch--current"
          title={product.color || 'Current'}
          onMouseEnter={() => setHoveredId('current')}
          onMouseLeave={() => setHoveredId(null)}
        >
          {product.images?.[0] ? (
            <img src={`${IMAGE_BASE_URL}${product.images[0]}`} alt={product.color || 'Current colour'} />
          ) : (
            <span style={{ backgroundColor: product.color_hex || '#d9cfbe' }} />
          )}
          <i className="ffd-swatch-tick">
            <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden="true">
              <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </i>
        </div>

        {/* other colours */}
        {product.color_variants?.map((variant) => (
          <button
            type="button"
            key={variant.p_id}
            className="ffd-swatch"
            title={variant.color || 'Variant'}
            aria-label={`View ${variant.color || 'variant'}`}
            onClick={() => onVariantClick(variant.p_slug)}
            onMouseEnter={() => setHoveredId(variant.p_id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            {variant.image && !brokenImg[variant.p_id] ? (
              <>
                <img
                  src={`${IMAGE_BASE_URL}${variant.image}`}
                  alt={variant.color || 'Colour variant'}
                  loading="lazy"
                  onError={() => setBrokenImg((b) => ({ ...b, [variant.p_id]: true }))}
                />
                {variant.color_hex && (
                  <i className="ffd-swatch-dot" style={{ backgroundColor: variant.color_hex }} />
                )}
              </>
            ) : (
              <span style={{ backgroundColor: variant.color_hex || '#d9cfbe' }} />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------
// Main ProductDetails Component
// -----------------------------------------------------------------
const ProductDetails = () => {
  const { p_slug } = useParams();
  const { user } = useUser();

  const navigate = useNavigate();
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isOpen, setIsOpen] = useState(true);

  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  const [isZooming, setIsZooming] = useState(false);
  const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [zoomScale, setZoomScale] = useState(2.5);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [isMobileZoomOpen, setIsMobileZoomOpen] = useState(false);
  const [mobileZoomImage, setMobileZoomImage] = useState<string>('');

  const token = localStorage.getItem('authToken');

  const readCart = (): CartItem[] => {
    try { return JSON.parse(localStorage.getItem('cart') || 'null') || []; }
    catch { return []; }
  };

  const writeCart = (cart: CartItem[]) => {
    localStorage.setItem('cart', JSON.stringify(cart));
    setCartItems(cart);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleImageMouseMove = (e: MouseEvent<HTMLElement>) => {
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const lensSize = 150;
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;
    let lensX = Math.max(0, Math.min(x - lensSize / 2, rect.width - lensSize));
    let lensY = Math.max(0, Math.min(y - lensSize / 2, rect.height - lensSize));
    setLensPosition({ x: lensX, y: lensY });
    setZoomPosition({ x: (lensX / rect.width) * 100, y: (lensY / rect.height) * 100 });
    setZoomScale(rect.width / lensSize);
  };

  const handleImageMouseEnter = () => setIsZooming(true);
  const handleImageMouseLeave = () => setIsZooming(false);

  const handleMobileImageClick = (imageUrl: string) => {
    setMobileZoomImage(imageUrl);
    setIsMobileZoomOpen(true);
  };
  const closeMobileZoom = () => {
    setIsMobileZoomOpen(false);
    setMobileZoomImage('');
  };

  // ── Handle variant navigation ──────────────────────────────────
  const handleVariantClick = (slug: string) => {
    // Reset state before navigating so the new product loads fresh
    setProduct(null);
    setSelectedSize('');
    setSelectedColor('');
    setQuantity(1);
    setCurrentImageIndex(0);
    setThumbsSwiper(null);
    navigate(`/product/${slug}`);
  };

  useEffect(() => {
    setCartItems(readCart());
    const onCartUpdated = () => setCartItems(readCart());
    window.addEventListener('cartUpdated', onCartUpdated);
    return () => window.removeEventListener('cartUpdated', onCartUpdated);
  }, []);

  useEffect(() => {
    const fetchProductDetails = async () => {
      setLoading(true);
      setError('');
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await fetch(API_ENDPOINTS.PRODUCTS, {
          method: 'POST',
          headers,
          body: JSON.stringify({ p_slug }),
        });
        const data = await response.json();
        let prod: Product | null = null;
        if (response.ok && data.status && data.data) {
          prod = Array.isArray(data.data) ? (data.data[0] || null) : data.data;
          setProduct(prod);
          if (prod?.size) {
            const available = Object.keys(prod.size).filter((s) => prod.size[s] > 0);
            if (available.length > 0) setSelectedSize(available[0]);
          }
        } else {
          setError(data.message || 'Failed to fetch product details.');
        }
      } catch {
        setError('An error occurred while fetching product details.');
      } finally {
        setLoading(false);
      }
    };
    fetchProductDetails();
  }, [p_slug, token]);

  if (loading) {
    return (
      <main className="ffd-page" aria-busy="true">
        <div className="ffd-container">
          <div className="ffd-crumbs"><span className="ffd-skel-line" style={{ width: 220, height: 12 }} /></div>
          <div className="ffd-grid">
            <div className="ffd-skel-media" />
            <div className="ffd-skel-info">
              <span className="ffd-skel-line" style={{ width: '70%', height: 44 }} />
              <span className="ffd-skel-line" style={{ width: 160, height: 32, marginTop: 20 }} />
              <span className="ffd-skel-line" style={{ width: '100%', height: 64, marginTop: 36 }} />
              <span className="ffd-skel-line" style={{ width: '100%', height: 64, marginTop: 16 }} />
              <span className="ffd-skel-line" style={{ width: '100%', height: 56, marginTop: 28 }} />
            </div>
          </div>
        </div>
      </main>
    );
  }
  if (error || !product) {
    return (
      <main className="ffd-page">
        <div className="ffd-container">
          <div className="ffd-state">
            <span className="ffd-state-icon" aria-hidden="true">{error ? '!' : '∅'}</span>
            <p>{error || 'Product not found.'}</p>
            <Link to="/products" className="ffh-btn">Browse products</Link>
          </div>
        </div>
      </main>
    );
  }

  const sizes = product.size ? Object.keys(product.size) : [];
  const isOutOfStock = sizes.length === 0 || sizes.every((s) => product.size[s] === 0);
  const isSelectedSizeOutOfStock = selectedSize && product.size[selectedSize] === 0;

  const canAddToCart = () => {
    if (isOutOfStock || isSelectedSizeOutOfStock) return false;
    if (sizes.length > 0 && selectedSize) return true;
    if (sizes.length === 0) return true;
    return false;
  };

  const toggleDescription = () => setIsOpen(!isOpen);

  const buildCartItem = (): CartItem => ({
    id: product.p_id,
    slug: product.p_slug,
    name: product.p_name,
    price: product.p_price,
    discount_percentage: product.discount_percentage || 0,
    image: product.images?.[0] ? `${IMAGE_BASE_URL}${product.images[0]}` : '',
    size: selectedSize || (sizes.length > 0 ? sizes[0] : ''),
    color: selectedColor || product.color || '',
    quantity,
    product_code: product.product_code,
    fabric_type: product.fabric_type,
    hsn_code: product.hsn_code,
  });

  const getSubtotal = (items: CartItem[]) =>
    items.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);

  const addToCart = (cartItem: CartItem, redirect = false) => {
    let cart = readCart();
    const existingIndex = cart.findIndex(
      (item) => item.id === cartItem.id && item.size === cartItem.size && item.color === cartItem.color
    );
    if (existingIndex > -1) {
      const availableQty = product.size[cartItem.size];
      const newQty = Number(cart[existingIndex].quantity) + Number(cartItem.quantity);
      if (newQty > availableQty) {
        toast.error(`Only ${availableQty} item${availableQty > 1 ? 's' : ''} available for size ${cartItem.size}`, { position: 'top-right', autoClose: 2000 });
        return;
      }
      cart[existingIndex].quantity = newQty;
    } else {
      cart.push({ ...cartItem });
    }
    writeCart(cart);
    if (redirect) {
      navigate('/cart');
    } else {
      setIsCartDrawerOpen(true);
      toast.success('Product added to cart!', { position: 'top-right', autoClose: 1500 });
    }
  };

  const removeFromCart = (index: number) => {
    const next = [...cartItems];
    next.splice(index, 1);
    writeCart(next);
  };

  const updateQty = (index: number, delta: number) => {
    const item = cartItems[index];
    if (!item) return;
    const max = item.size && product.size?.[item.size] ? product.size[item.size] : Infinity;
    const next = [...cartItems];
    next[index] = { ...item, quantity: Math.min(Math.max(1, Number(item.quantity) + delta), max) };
    writeCart(next);
  };

  // ---------- derived display values ----------
  const images = product.images || [];
  const basePrice = Number(product.p_price);
  const discPct = Number(product.discount_percentage) || 0;
  const showVendorPrice = !!token && !!user?.created_by && discPct > 0;
  const salePrice = basePrice - (basePrice * discPct) / 100;
  const stockLeft = selectedSize ? Number(product.size?.[selectedSize] ?? 0) : 0;
  const lowStock = !isOutOfStock && !isSelectedSizeOutOfStock && selectedSize !== '' && stockLeft > 0 && stockLeft <= 5;
  const addDisabled = !canAddToCart();

  const priceBlock = showVendorPrice ? (
    <>
      <span className="ffd-price ffd-price--sale">₹{salePrice.toFixed(2)}</span>
      <span className="ffd-price-old">₹{basePrice.toFixed(2)}</span>
      <span className="ffd-price-off">{product.discount_percentage}% off</span>
    </>
  ) : (
    <span className="ffd-price">₹{basePrice.toFixed(2)}</span>
  );

  return (
    <>
      <main className="ffd-page">
        <section className="ffd-top">
          <div className="ffd-container">

            {/* ── Breadcrumbs ── */}
            <nav className="ffd-crumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <Link to="/products">Products</Link>
              <span aria-hidden="true">/</span>
              <span className="is-current">{product.p_name}</span>
            </nav>

            <div className={`ffd-grid${isZooming ? ' is-zooming' : ''}`}>

              {/* ══ GALLERY ══ */}
              <div className="ffd-gallery-col">
                <div className="ffd-gallery">
                  <div className="ffd-thumbs">
                    <Swiper
                      modules={[Navigation, Thumbs]}
                      onSwiper={setThumbsSwiper}
                      spaceBetween={10}
                      slidesPerView="auto"
                      direction="vertical"
                      freeMode={true}
                      watchSlidesProgress={true}
                      breakpoints={{
                        0: { direction: 'horizontal', slidesPerView: 4 },
                        992: { direction: 'vertical', slidesPerView: 'auto' },
                      }}
                    >
                      {images.map((image, index) => (
                        <SwiperSlide key={index}>
                          <img src={`${IMAGE_BASE_URL}${image}`} alt={`${product.p_name} thumbnail ${index + 1}`} />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>

                  <div className="ffd-main">
                    <Swiper
                      modules={[Navigation, Thumbs]}
                      navigation={{ nextEl: '.custom-arrow-right-pd', prevEl: '.custom-arrow-left-pd' }}
                      thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                      onSlideChange={(swiper) => setCurrentImageIndex(swiper.activeIndex)}
                    >
                      {images.map((image, index) => (
                        <SwiperSlide key={index}>
                          {/* desktop: hover zoom */}
                          <div
                            className="ffd-zoom d-none d-lg-block"
                            onMouseMove={handleImageMouseMove}
                            onMouseEnter={handleImageMouseEnter}
                            onMouseLeave={handleImageMouseLeave}
                          >
                            <img src={`${IMAGE_BASE_URL}${image}`} alt={`${product.p_name} ${index + 1}`} />
                            {isZooming && currentImageIndex === index && (
                              <div className="ffd-lens" style={{ left: `${lensPosition.x}px`, top: `${lensPosition.y}px` }} />
                            )}
                          </div>

                          {/* mobile: tap to zoom */}
                          <div className="ffd-zoom d-lg-none" onClick={() => handleMobileImageClick(`${IMAGE_BASE_URL}${image}`)}>
                            <img src={`${IMAGE_BASE_URL}${image}`} alt={`${product.p_name} ${index + 1}`} />
                            <span className="ffd-zoom-icon"><FontAwesomeIcon icon={faSearchPlus} /></span>
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>

                    <button type="button" className="ffd-arrow ffd-arrow--left custom-arrow-left-pd" aria-label="Previous image">
                      <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M11 14L6 9l5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                    <button type="button" className="ffd-arrow ffd-arrow--right custom-arrow-right-pd" aria-label="Next image">
                      <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M7 4l5 5-5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>

                    {showVendorPrice && <span className="ffd-badge">−{product.discount_percentage}%</span>}

                  </div>
                </div>

                {/* zoom panel – sits over the info column (which fades out while zooming) */}
                {isZooming && images[currentImageIndex] && (
                  <div className="ffd-zoom-view d-none d-lg-block" aria-hidden="true">
                    <img
                      src={`${IMAGE_BASE_URL}${images[currentImageIndex]}`}
                      alt=""
                      style={{
                        width: `${zoomScale * 100}%`,
                        height: `${zoomScale * 100}%`,
                        left: `-${zoomPosition.x * zoomScale}%`,
                        top: `-${zoomPosition.y * zoomScale}%`,
                      }}
                    />
                  </div>
                )}
              </div>

              {/* ══ INFO ══ */}
              <div className="ffd-info">
                {product.fabric_type && (
                  <p className="ffh-eyebrow ffh-eyebrow--left">{product.fabric_type}</p>
                )}
                <h1 className="ffd-title">{product.p_name}</h1>

                <div className="ffd-price-row">{priceBlock}</div>

                <p className={`ffd-stock${isOutOfStock ? ' is-out' : lowStock ? ' is-low' : ''}`}>
                  <i />
                  {isOutOfStock
                    ? 'Out of stock'
                    : lowStock
                      ? `Only ${stockLeft} left in this size`
                      : 'In stock'}
                </p>

                <div className="ffd-rule" />

                {/* Colour */}
                <ColorVariants product={product} onVariantClick={handleVariantClick} />

                {/* Size */}
                {sizes.length > 0 && !isOutOfStock && (
                  <div className="ffd-block">
                    <div className="ffd-label-row">
                      <span className="ffd-label">Size</span>
                    </div>
                    <div className="ffd-sizes">
                      {sizes.map((size) => {
                        const qty = product.size[size];
                        const isDisabled = qty === 0;
                        return (
                          <button
                            key={size}
                            type="button"
                            className={`ffd-size${selectedSize === size ? ' is-active' : ''}${isDisabled ? ' is-disabled' : ''}`}
                            onClick={() => { if (!isDisabled) { setSelectedSize(size); setQuantity(1); } }}
                            disabled={isDisabled}
                          >
                            {size} <small>{isDisabled ? '(0)' : `(${qty})`}</small>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Quantity */}
                {!isOutOfStock && !isSelectedSizeOutOfStock && (
                  <div className="ffd-block ffd-block--inline">
                    <div className="ffd-label-row">
                      <span className="ffd-label">Quantity</span>
                    </div>
                    <div className="ffd-qty">
                      <button type="button" onClick={() => { if (quantity > 1) setQuantity(quantity - 1); }} disabled={quantity <= 1} aria-label="Decrease quantity">
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                      <span className="ffd-qty-num">{quantity}</span>
                      <button
                        type="button"
                        aria-label="Increase quantity"
                        onClick={() => {
                          const maxQty = selectedSize && product.size[selectedSize] ? product.size[selectedSize] : Infinity;
                          if (quantity < maxQty) { setQuantity(quantity + 1); }
                          else { toast.error(`Only ${maxQty} item${maxQty > 1 ? 's' : ''} available for size ${selectedSize}`, { position: 'top-right', autoClose: 2000 }); }
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="ffd-actions">
                  <button type="button" className="ffh-btn" onClick={() => addToCart(buildCartItem(), false)} disabled={addDisabled}>
                    Add to cart
                  </button>
                  <button type="button" className="ffh-btn ffh-btn--gold" onClick={() => addToCart(buildCartItem(), true)} disabled={addDisabled}>
                    Buy now
                  </button>
                </div>

                <ul className="ffd-trust">
                  <li>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" /><circle cx="7.5" cy="17.5" r="1.5" /><circle cx="17.5" cy="17.5" r="1.5" /></svg>
                    Free shipping
                  </li>
                  <li>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></svg>
                    Secure payments
                  </li>
                  <li>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 109-9M3 4v5h5" /></svg>
                    Easy returns
                  </li>
                </ul>

                {/* Description */}
                <div className="ffd-acc">
                  <button type="button" className="ffd-acc-head" onClick={toggleDescription} aria-expanded={isOpen}>
                    <span>Description</span>
                    {isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                  </button>
                  {isOpen && (
                    <div className="ffd-acc-body">
                      <p>{product.p_description}</p>
                    </div>
                  )}
                </div>

                {/* Specifications */}
                <div className="ffd-acc">
                  <div className="ffd-acc-head ffd-acc-head--static"><span>Specifications</span></div>
                  <dl className="ffd-specs">
                    <div><dt>Fabric</dt><dd>{product.fabric_type}</dd></div>
                    <div><dt>HSN code</dt><dd>{product.hsn_code}</dd></div>
                    <div><dt>Product code</dt><dd>{product.product_code}</dd></div>
                    {token && user?.created_by && (
                      <div><dt>Discount</dt><dd>{product.discount_percentage}%</dd></div>
                    )}
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Reviews ── */}
        <ReviewsSection productId={product.p_id} />

        {/* ── Mobile sticky buy bar ── */}
        <div className="ffd-sticky">
          <div className="ffd-sticky-info">
            <strong>{product.p_name}</strong>
            <span>{showVendorPrice ? `₹${salePrice.toFixed(2)}` : `₹${basePrice.toFixed(2)}`}</span>
          </div>
          <button type="button" className="ffh-btn" onClick={() => addToCart(buildCartItem(), false)} disabled={addDisabled}>
            Add to cart
          </button>
        </div>

        {/* Mobile Zoom Modal */}
        {isMobileZoomOpen && (
          <div className="ffd-mzoom" onClick={closeMobileZoom}>
            <button type="button" className="ffd-mzoom-close" onClick={closeMobileZoom} aria-label="Close"><FontAwesomeIcon icon={faTimes} /></button>
            <div className="ffd-mzoom-content" onClick={(e) => e.stopPropagation()}>
              <img src={mobileZoomImage} alt="Zoomed product" />
            </div>
          </div>
        )}

        {/* Cart drawer */}
        {isCartDrawerOpen && <div className="ffd-drawer-backdrop" onClick={() => setIsCartDrawerOpen(false)} />}

        <aside className={`ffd-drawer${isCartDrawerOpen ? ' is-open' : ''}`} aria-hidden={!isCartDrawerOpen}>
          <div className="ffd-drawer-head">
            <h3>Your cart</h3>
            <button type="button" className="ffd-drawer-close" onClick={() => setIsCartDrawerOpen(false)} aria-label="Close cart">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="ffd-drawer-body">
            {cartItems.length > 0 ? cartItems.map((item, index) => (
              <div key={`${item.id}-${item.size}-${item.color}-${index}`} className="ffd-drawer-item">
                <img src={item.image} alt={item.name} />
                <div className="ffd-drawer-info">
                  <div className="ffd-drawer-name">{item.name}</div>
                  <div className="ffd-drawer-meta">
                    {item.size && <>Size: {item.size}</>}
                    {item.color && <> · {item.color}</>}
                    <> · ₹{Number(item.price).toFixed(2)}</>
                  </div>
                  <div className="ffd-drawer-ctrl">
                    <div className="ffd-qty ffd-qty--sm">
                      <button type="button" onClick={() => updateQty(index, -1)} aria-label="Decrease">−</button>
                      <span className="ffd-qty-num">{item.quantity}</span>
                      <button type="button" onClick={() => updateQty(index, +1)} aria-label="Increase">+</button>
                    </div>
                    <button type="button" className="ffd-link" onClick={() => removeFromCart(index)}>Remove</button>
                  </div>
                </div>
                <div className="ffd-drawer-total">₹{(Number(item.price) * Number(item.quantity)).toFixed(2)}</div>
              </div>
            )) : <p className="ffd-drawer-empty">Your cart is empty.</p>}
          </div>

          <div className="ffd-drawer-foot">
            <div className="ffd-drawer-sub">
              <span>Subtotal</span>
              <strong>₹{getSubtotal(cartItems).toFixed(2)}</strong>
            </div>
            <button
              type="button"
              className="ffh-btn ffc-btn--block"
              disabled={cartItems.length === 0}
              onClick={() => { setIsCartDrawerOpen(false); navigate('/cart'); }}
            >
              Go to cart
            </button>
          </div>
        </aside>

        <ToastContainer />
      </main>
    </>
  );
};

export default ProductDetails;